import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff, ViewInArOutlined as ViewerIcon, LockOutlined as LockIcon } from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import { idocService, type IdocAccount, type IdocApiError } from '../../services/idocService';

interface IdocConnectDialogProps {
  open: boolean;
  onClose: () => void;
  onConnected: (account: IdocAccount) => void;
  /** E-mail já conhecido (reconexão de conta expirada). */
  defaultEmail?: string | null;
}

const IdocConnectDialog: React.FC<IdocConnectDialogProps> = ({ open, onClose, onConnected, defaultEmail }) => {
  const { user, isAdmin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [label, setLabel] = useState('');
  const [professionalId, setProfessionalId] = useState('');
  const [professionals, setProfessionals] = useState<Array<{ id: string; full_name: string }>>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setEmail(defaultEmail || '');
    setPassword('');
    setLabel('');
    setError(null);
    setShowPassword(false);
    setProfessionalId(user?.professional_id || '');
    if (isAdmin) {
      axios
        .get(getApiUrl('/clinic/professionals'), { params: { is_active: true, limit: 200 } })
        .then((res) => setProfessionals(Array.isArray(res.data?.data) ? res.data.data : []))
        .catch(() => setProfessionals([]));
    }
  }, [open, defaultEmail, isAdmin, user?.professional_id]);

  const submit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!email.trim() || !password) {
      setError('Informe o e-mail e a senha da conta iDoc.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const account = await idocService.linkAccount({
        email: email.trim(),
        password,
        label: label.trim() || undefined,
        professional_id: professionalId || null,
      });
      setPassword('');
      onConnected(account);
      onClose();
    } catch (e) {
      setError((e as IdocApiError).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <form onSubmit={submit}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <ViewerIcon sx={{ color: '#0D9488' }} /> Conectar conta iDoc
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Use o mesmo login do portal iDoc da Radio Memory. As documentações dos pacientes desta conta passam a
            aparecer no prontuário.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="E-mail do iDoc"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Senha do iDoc"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowPassword((v) => !v)} aria-label="Mostrar senha">
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Apelido (opcional)"
            placeholder="Ex.: Dra. Ana — Ortodontia"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            sx={{ mb: isAdmin ? 2 : 0 }}
          />
          {isAdmin && (
            <FormControl fullWidth size="small">
              <InputLabel>Profissional (opcional)</InputLabel>
              <Select value={professionalId} label="Profissional (opcional)" onChange={(e) => setProfessionalId(String(e.target.value))}>
                <MenuItem value=""><em>Conta da clínica</em></MenuItem>
                {professionals.map((p) => <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 2, color: '#64748b' }}>
            <LockIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">A senha não é guardada: o Klinvia recebe só um token de acesso cifrado.</Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
            sx={{ bgcolor: '#0D9488', '&:hover': { bgcolor: '#0F766E' }, textTransform: 'none', borderRadius: 2 }}
          >
            Conectar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default IdocConnectDialog;
